import axios from "axios";
import { msalInstance } from "../auth/AuthProvider";
import { tokenRequest } from "../auth/authConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    headers: {
        "Content-Type": "application/json",
    },
});

const resolveAccount = () => {
    return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
};

api.interceptors.request.use(
    async (config) => {
        try {
            const account = resolveAccount();
            
            if (account) {
                const response = await msalInstance.acquireTokenSilent({
                    ...tokenRequest,
                    account: account,
                });
                
                config.headers.Authorization = `Bearer ${response.accessToken}`;
            }
        } catch (error) {
            console.error("Failed to acquire token:", error);
            
            if (error.name === "InteractionRequiredAuthError") {
                try {
                    const response = await msalInstance.acquireTokenPopup(tokenRequest);
                    config.headers.Authorization = `Bearer ${response.accessToken}`;
                } catch (popupError) {
                    console.error("Popup token acquisition failed:", popupError);
                }
            }
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
                const account = resolveAccount();
                const response = await msalInstance.acquireTokenSilent({
                    ...tokenRequest,
                    account: account,
                    forceRefresh: true,
                });
                
                originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
                return api(originalRequest);
            } catch (tokenError) {
                console.error("Failed to refresh token:", tokenError);
                return Promise.reject(tokenError);
            }
        }
        
        return Promise.reject(error);
    }
);

export const apiService = {
    getUserProfile: async () => {
        const response = await api.get("/auth/me");
        return response.data;
    },

    getUserContactSettings: async () => {
        const response = await api.get("/profile/contact-settings");
        return response.data;
    },

    updateUserContactSettings: async (payload) => {
        const response = await api.put("/profile/contact-settings", payload);
        return response.data;
    },
    
    createEvent: async (eventData) => {
        const response = await api.post("/events", eventData);
        return response.data;
    },
    
    getEvents: async (statusFilter = null, mineOnly = false) => {
        const params = {};
        if (statusFilter) {
            params.status_filter = statusFilter;
        }
        if (mineOnly) {
            params.scope = "mine";
        }
        const response = await api.get("/events", { params });
        return response.data;
    },
    
    getEvent: async (eventId) => {
        const response = await api.get(`/events/${eventId}`);
        return response.data;
    },

    getEventCategories: async () => {
        const response = await api.get("/events/categories");
        return response.data;
    },

    getKeytickOrganisations: async (payload) => {
        const response = await api.post("/keytick/organisations", payload);
        return response.data;
    },
    
    updateEvent: async (eventId, eventData) => {
        const response = await api.put(`/events/${eventId}`, eventData);
        return response.data;
    },

    updateEventImages: async (eventId, eventData) => {
        const response = await api.put(`/events/${eventId}/images`, eventData);
        return response.data;
    },

    assignEvent: async (eventId, organizerEmail) => {
        const response = await api.put(`/events/${eventId}`, { organizer_email: organizerEmail });
        return response.data;
    },
    
    deleteEvent: async (eventId, forceWpDelete = false) => {
        const response = await api.delete(`/events/${eventId}`, {
            params: forceWpDelete ? { force_wp_delete: true } : {},
        });
        return response.data;
    },

    uploadEventImage: async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post("/events/upload-image", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        return response.data;
    },
    
    // Admin APIs
    getPendingEvents: async () => {
        const response = await api.get("/admin/events/pending");
        return response.data;
    },
    
    approveEvent: async (eventId, status, rejectionMessage = "") => {
        const response = await api.patch(`/admin/events/${eventId}/approve`, {
            status,
            rejection_message: rejectionMessage
        });
        return response.data;
    },
    
    publishEvent: async (eventId) => {
        const response = await api.post(`/admin/events/${eventId}/publish`);
        return response.data;
    },

    syncEventToWordpress: async (eventId) => {
        const response = await api.post(`/admin/events/${eventId}/sync-wordpress`);
        return response.data;
    },

    recallEventFromWordpress: async (eventId) => {
        const response = await api.post(`/admin/events/${eventId}/recall-wordpress`);
        return response.data;
    },

    exportGemaSetlistXlsx: async (eventId, includeNonRequired = false) => {
        const response = await api.get(`/admin/events/${eventId}/gema-setlist-export`, {
            params: includeNonRequired ? { include_non_required: true } : {},
            responseType: "blob",
        });
        return response;
    },

    clearEventSetlist: async (eventId) => {
        const response = await api.post(`/admin/events/${eventId}/setlist/clear`);
        return response.data;
    },

    importWordpressEvents: async () => {
        const response = await api.post("/admin/events/import-wordpress");
        return response.data;
    },
    
    getUsers: async () => {
        const response = await api.get("/admin/users");
        return response.data;
    },

    getNotificationSettings: async () => {
        const response = await api.get("/admin/notification-settings");
        return response.data;
    },

    updateNotificationSettings: async (settings) => {
        const response = await api.put("/admin/notification-settings", settings);
        return response.data;
    },

    sendNotificationTest: async (recipient) => {
        const response = await api.post("/admin/notification-test", { recipient });
        return response.data;
    },

    getSmtpOauthStatus: async () => {
        const response = await api.get("/admin/smtp-oauth/status");
        return response.data;
    },

    getScreenSettings: async () => {
        const response = await api.get("/admin/screen-settings");
        return response.data;
    },

    updateScreenSettings: async (settings) => {
        const response = await api.put("/admin/screen-settings", settings);
        return response.data;
    },

    startSmtpOauthDeviceCode: async () => {
        const response = await api.post("/admin/smtp-oauth/device-code/start");
        return response.data;
    },

    pollSmtpOauthDeviceCode: async (deviceCode) => {
        const response = await api.post("/admin/smtp-oauth/device-code/poll", { device_code: deviceCode });
        return response.data;
    },

    createEventNotice: async (payload) => {
        const response = await api.post("/admin/event-notices", payload);
        return response.data;
    },

    getDeadlines: async () => {
        const response = await api.get("/admin/deadlines");
        return response.data;
    },

    createDeadline: async (payload) => {
        const response = await api.post("/admin/deadlines", payload);
        return response.data;
    },

    dispatchGemaReminders: async () => {
        const response = await api.post("/admin/gema/dispatch");
        return response.data;
    },

    getGemaExternalData: async (includeVeranstaltungen = true) => {
        const response = await api.get("/admin/gema/external-data", {
            params: { include_veranstaltungen: includeVeranstaltungen },
        });
        return response.data;
    },

    getGemaDashboard: async () => {
        const response = await api.get("/admin/gema/dashboard");
        return response.data;
    },

    updateGemaManualStatus: async (eventId, done, note = "") => {
        const response = await api.post("/admin/gema/manual-status", {
            event_id: eventId,
            done,
            note,
        });
        return response.data;
    },

    sendGemaReminder: async (eventId, message = "") => {
        const response = await api.post("/admin/gema/reminders/send", {
            event_id: eventId,
            message,
        });
        return response.data;
    },

    reloginGema: async () => {
        const response = await api.post("/admin/gema/relogin");
        return response.data;
    },

    getGemaTarif: async (tarifId, eventId = null, beginDate = null) => {
        const response = await api.get(`/admin/gema/tarif/${encodeURIComponent(tarifId)}`, {
            params: { event_id: eventId || undefined, begin_date: beginDate || undefined },
        });
        return response.data;
    },

    getGemaRaeume: async (nutzungsortId) => {
        const response = await api.get(`/admin/gema/raeume/${encodeURIComponent(nutzungsortId)}`);
        return response.data;
    },

    getGemaTemplates: async (eventId, occurrenceId = null) => {
        const response = await api.get("/admin/gema/templates", {
            params: { event_id: eventId, occurrence_id: occurrenceId || undefined },
        });
        return response.data;
    },

    getGemaTemplateCopy: async (lizenzId, position, eventId, occurrenceId = null) => {
        const response = await api.get(
            `/admin/gema/templates/${encodeURIComponent(lizenzId)}/${encodeURIComponent(position || "10")}/copy`,
            { params: { event_id: eventId, occurrence_id: occurrenceId || undefined } },
        );
        return response.data;
    },

    saveGemaTemplateRule: async (match, template) => {
        const response = await api.post("/admin/gema/template-rules", { match, template });
        return response.data;
    },

    getGemaCheckoutPreview: async (rechnungsadresseId = null, bankdatenId = null) => {
        const response = await api.get("/admin/gema/checkout-preview", {
            params: {
                rechnungsadresse_id: rechnungsadresseId || undefined,
                bankdaten_id: bankdatenId || undefined,
            },
        });
        return response.data;
    },

    parseGemaSetlistText: async (text) => {
        const response = await api.post("/admin/gema/setlist/parse", { text });
        return response.data;
    },

    searchGemaWerke: async (entries) => {
        const response = await api.post("/admin/gema/werksuche", { entries });
        return response.data;
    },

    getAdminGemaSetlist: async (eventId, occurrenceId = null) => {
        const response = await api.get(`/admin/events/${eventId}/gema-setlist`, {
            params: { occurrence_id: occurrenceId || undefined },
        });
        return response.data;
    },

    updateAdminGemaSetlist: async (eventId, payload) => {
        const response = await api.put(`/admin/events/${eventId}/gema-setlist`, payload);
        return response.data;
    },

    submitGemaSetlist: async (payload) => {
        const response = await api.post("/admin/gema/setlist/submit", payload);
        return response.data;
    },

    calculateGemaTarif: async (tarifId, payload) => {
        const response = await api.post(`/admin/gema/tarif/calculate/${encodeURIComponent(tarifId)}`, payload);
        return response.data;
    },

    searchGemaTarife: async (stichwort) => {
        const response = await api.get("/admin/gema/tarif-suche", { params: { q: stichwort } });
        return response.data;
    },

    previewGemaMeldung: async (payload) => {
        const response = await api.post("/admin/gema/melden/preview", payload);
        return response.data;
    },
    submitGemaMeldung: async (payload) => {
        const response = await api.post("/admin/gema/melden", payload);
        return response.data;
    },

    markGemaCancellationDone: async (eventId, registrationKey, done = true, note = "") => {
        const response = await api.post("/admin/gema/cancellation-done", {
            event_id: eventId,
            registration_key: registrationKey,
            done,
            note,
        });
        return response.data;
    },

    acknowledgeGemaRegistrationHint: async (eventId, registrationKey, kind, occurrenceId = null) => {
        const response = await api.post("/admin/gema/registration-ack", {
            event_id: eventId,
            registration_key: registrationKey,
            kind,
            occurrence_id: occurrenceId,
        });
        return response.data;
    },

    updateDeadline: async (deadlineId, payload) => {
        const response = await api.put(`/admin/deadlines/${deadlineId}`, payload);
        return response.data;
    },

    deleteDeadline: async (deadlineId) => {
        const response = await api.delete(`/admin/deadlines/${deadlineId}`);
        return response.data;
    },

    updateUserRole: async (userId, role) => {
        const response = await api.patch(`/admin/users/${userId}/role`, { role });
        return response.data;
    },
    
    // Public API
    getApprovedEvents: async () => {
        const response = await api.get("/public/events");
        return response.data;
    },

    getPublicSetlist: async (token) => {
        const response = await axios.get(`${API}/public/setlists/${token}`);
        return response.data;
    },

    downloadPublicSetlistTemplate: async (token) => {
        return axios.get(`${API}/public/setlists/${token}/excel-template`, {
            responseType: "blob",
        });
    },

    downloadPublicSetlistExcel: async (token) => {
        return axios.get(`${API}/public/setlists/${token}/excel`, {
            responseType: "blob",
        });
    },

    uploadPublicSetlistExcel: async (token, file) => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/public/setlists/${token}/excel`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    },

    submitPublicSetlist: async (token, payload) => {
        const response = await axios.post(`${API}/public/setlists/${token}`, payload);
        return response.data;
    },
};

export default api;
